import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  useCreateProjectStep1Mutation,
  useAddProjectImagesMutation,
  useAddProjectDocumentsMutation,
  useGetAllCategoriesQuery,
  useGetAllProjectsQuery,
  useSearchLocationQuery
} from '../../store/slices/apiSlice';
import ImageUploadForm from '../../components/admin/ImageUploadForm';

import {
  Plus,
  Upload,
  X,
  FileText,
  Image as ImageIcon,
  MapPin,
  CheckCircle,
  Building2,
  FolderOpen
} from 'lucide-react';
import { API_BASE_URL } from '../../config/api';


import { Input } from '../../components/ui/Input';

// Project creation validation schema
const projectSchema = yup.object({
  title: yup.string().required('Title is required'),
  short_description: yup.string().required('Short description is required'),
  full_description: yup.string().required('Full description is required'),
  category_id: yup.number().required('Category is required').min(1),
  location: yup.string().required('Location is required'),
  funding_goal: yup.number().required('Funding goal is required').min(1),
  min_allowed_investment: yup.number().required('Minimum investment is required').min(1),
  max_allowed_investment: yup.number().required('Maximum investment is required').min(1),
  auto_close_on_goal: yup.boolean().required(),
  deadline: yup.string().optional(),
  start_date: yup.string().optional(),
  expected_roi_percentage: yup.number().required('Expected ROI percentage is required').min(0).max(100),
  roi_duration_months: yup.number().required('ROI duration in months is required').min(1),
  currency_code: yup.string().required('Currency code is required'),
});

type ProjectFormData = yup.InferType<typeof projectSchema>;

interface LocationSuggestion {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
  terms: Array<{
    offset: number;
    value: string;
  }>;
  types: string[];
}

interface LatLongResponse {
  success: boolean;
  status: number;
  message: string;
  data: string; // Format: "latitude, longitude"
}

const CreateProject: React.FC = () => {
  const [createProject, { isLoading: isCreatingProject }] = useCreateProjectStep1Mutation();
  const [addImages, { isLoading: isAddingImages }] = useAddProjectImagesMutation();
  const [addDocuments, { isLoading: isAddingDocuments }] = useAddProjectDocumentsMutation();
  const { data: categoriesResponse } = useGetAllCategoriesQuery();
  const { data: projectsResponse, refetch: refetchProjects } = useGetAllProjectsQuery({
    page: 1,
    limit: 10000,
    search: '',
  });

  const categories = categoriesResponse?.data || [];
  const projects = projectsResponse?.data || [];

  const [activeTab, setActiveTab] = useState<'create' | 'images' | 'documents'>('create');
  const [selectedProjectForImages, setSelectedProjectForImages] = useState<number | ''>('');
  const [selectedProjectForDocuments, setSelectedProjectForDocuments] = useState<string>('');
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [selectedDocuments, setSelectedDocuments] = useState<{ name: string; file: File }[]>([]);
  const [locationSearch, setLocationSearch] = useState('');
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState<LocationSuggestion[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [selectedLatitude, setSelectedLatitude] = useState<number | null>(null);
  const [selectedLongitude, setSelectedLongitude] = useState<number | null>(null);
  const [status, setStatus] = useState<'active' | 'draft' | 'closed'>('active');
  const [documentNameInput, setDocumentNameInput] = useState('');

  // Farm Sponsorship Project Mode
  const [projectTypeMode, setProjectTypeMode] = useState<'standard' | 'farm'>('standard');
  const [farmCapacity, setFarmCapacity] = useState('150');
  const [farmHarvestDate, setFarmHarvestDate] = useState('');

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    reset,
  } = useForm<ProjectFormData>({
    resolver: yupResolver(projectSchema),
    mode: 'onChange',
    defaultValues: {
      auto_close_on_goal: true,
    }
  });

  const watchedValues = watch();

  // Auto-apply Agriculture category and clean defaults when Farm Sponsorship Mode is selected
  React.useEffect(() => {
    if (projectTypeMode === 'farm') {
      const agriCat = categories.find((c: any) => c.name.toLowerCase().includes('agri') || c.name.toLowerCase().includes('farm'));
      if (agriCat) {
        setValue('category_id', agriCat.category_id, { shouldValidate: true });
      } else if (categories.length > 0) {
        setValue('category_id', categories[0].category_id, { shouldValidate: true });
      }

      setValue('expected_roi_percentage', 24, { shouldValidate: true });
      setValue('roi_duration_months', 12, { shouldValidate: true });
      setValue('min_allowed_investment', 100, { shouldValidate: true });
      setValue('max_allowed_investment', 1000000, { shouldValidate: true });
      setValue('currency_code', 'NGN', { shouldValidate: true });
    }
  }, [projectTypeMode, categories, setValue]);

  // Search location using the API with enhanced authentication
  const searchLocation = async (query: string) => {
    if (!query.trim()) {
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);
      return;
    }

    console.log('Searching location with token:', token.substring(0, 20) + '...');

    setIsSearchingLocation(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/search-location?input=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.data) {
        setLocationSuggestions(data.data);
        setShowLocationSuggestions(true);
      } else {
        setLocationSuggestions([]);
        setShowLocationSuggestions(false);
        if (data.message) {
          toast.error(`Location search failed: ${data.message}`);
        }
      }
    } catch (error: any) {
      console.error('Location search error:', error);
      setLocationSuggestions([]);
      setShowLocationSuggestions(false);

      // Handle specific error cases
      if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Location search failed. Please try again.');
      }
    } finally {
      setIsSearchingLocation(false);
    }
  };

  // Get latitude and longitude for selected location
  const getLatLong = async (address: string): Promise<{ latitude: number; longitude: number } | null> => {
    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      return null;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      return null;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      return null;
    }

    console.log('Getting lat/long with token:', token.substring(0, 20) + '...');

    try {
      const response = await fetch(`${API_BASE_URL}/admin/get-lat-long?address=${encodeURIComponent(address)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success && data.data) {
        // Handle both string format (comma-separated) and object format
        let latitude: number, longitude: number;

        if (typeof data.data === 'string') {
          const [lat, lng] = data.data.split(',').map(Number);
          latitude = lat;
          longitude = lng;
        } else if (typeof data.data === 'object' && data.data.latitude && data.data.longitude) {
          latitude = data.data.latitude;
          longitude = data.data.longitude;
        } else {
          console.error('Unexpected data format:', data.data);
          return null;
        }

        return {
          latitude,
          longitude
        };
      } else {
        console.error('API returned error:', data.message);
        toast.error(`Failed to get coordinates: ${data.message || 'Unknown error'}`);
        return null;
      }
    } catch (error: any) {
      console.error('Get lat/long error:', error);

      // Handle specific error cases
      if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 500) {
        toast.error('Server error. Please try again later.');
      } else {
        toast.error('Failed to get coordinates. Please try again.');
      }

      return null;
    }
  };

  const handleLocationSearch = (value: string) => {
    setLocationSearch(value);
    setValue('location', value);

    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchLocation(value);
    }, 300);

    return () => clearTimeout(timeoutId);
  };

  const selectLocation = async (location: LocationSuggestion) => {
    setSelectedLocation(location.description);
    setLocationSearch(location.description);
    setValue('location', location.description);
    setShowLocationSuggestions(false);

    // Get latitude and longitude for the selected location
    const latLong = await getLatLong(location.description);
    if (latLong) {
      setSelectedLatitude(latLong.latitude);
      setSelectedLongitude(latLong.longitude);
    }
  };

  // Function to convert unsupported image formats to JPEG
  const convertImageToJPEG = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        ctx?.drawImage(img, 0, 0);

        canvas.toBlob((blob) => {
          if (blob) {
            const convertedFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(convertedFile);
          } else {
            reject(new Error('Failed to convert image'));
          }
        }, 'image/jpeg', 0.9);
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length + selectedImages.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    try {
      const processedImages: File[] = [];

      for (const file of imageFiles) {
        const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

        if (supportedFormats.includes(file.type.toLowerCase())) {
          processedImages.push(file);
        } else {
          // Convert unsupported formats to JPEG
          try {
            const convertedFile = await convertImageToJPEG(file);
            processedImages.push(convertedFile);
            toast.success(`Converted ${file.name} to JPEG format`);
          } catch (error) {
            console.error('Failed to convert image:', file.name, error);
            toast.error(`Failed to convert ${file.name}. Please use a supported format.`);
          }
        }
      }

      setSelectedImages(prev => [...prev, ...processedImages]);
    } catch (error) {
      console.error('Error processing images:', error);
      toast.error('Error processing images. Please try again.');
    }
  };

  const removeImage = (index: number) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    // Support more file types as per API requirements
    const documentFiles = files.filter(file => {
      const supportedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'application/zip',
        'application/x-rar-compressed'
      ];

      return supportedTypes.includes(file.type.toLowerCase()) ||
        file.type.includes('document') ||
        file.type.includes('text');
    });

    if (documentFiles.length + selectedDocuments.length > 5) {
      toast.error('Maximum 5 documents allowed');
      return;
    }

    // Check file size (10MB limit)
    const oversizedFiles = documentFiles.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Files too large: ${oversizedFiles.map(f => f.name).join(', ')}. Maximum size is 10MB per file.`);
      return;
    }

    const newDocuments = documentFiles.map(file => ({
      name: file.name,
      file: file
    }));

    setSelectedDocuments(prev => [...prev, ...newDocuments]);

    toast.success(`${newDocuments.length} document(s) selected successfully`);
  };

  const removeDocument = (index: number) => {
    setSelectedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmitProject = async (data: ProjectFormData) => {
    // Check if we have latitude and longitude
    if (!selectedLatitude || !selectedLongitude) {
      toast.error('Please select a valid location from the suggestions. Type in the location field and choose from the dropdown.');
      return;
    }

    try {
      // Helper function to safely extract values
      const safeString = (value: any): string => {
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object') {
          // Handle DOM event objects
          if (value.target && value.target.value) return String(value.target.value);
          // Handle select option objects
          if (value.value) return String(value.value);
          // Handle any other object with a value property
          if (value.label) return String(value.label);
        }
        return String(value || '');
      };

      const safeNumber = (value: any): number => {
        if (typeof value === 'number') return value;
        if (value && typeof value === 'object') {
          // Handle DOM event objects
          if (value.target && value.target.value) return Number(value.target.value) || 0;
          // Handle select option objects
          if (value.value) return Number(value.value) || 0;
        }
        return Number(value) || 0;
      };

      const safeBoolean = (value: any): boolean => {
        if (typeof value === 'boolean') return value;
        if (value && typeof value === 'object') {
          // Handle DOM event objects
          if (value.target && value.target.checked !== undefined) return Boolean(value.target.checked);
          // Handle select option objects
          if (value.value !== undefined) return Boolean(value.value);
        }
        return Boolean(value);
      };

      // Validate status
      const validStatuses = ['active', 'draft', 'closed'];
      if (!validStatuses.includes(status)) {
        toast.error('Invalid project status. Please select a valid status.');
        return;
      }

      // Create a clean, serializable data object with explicit type conversion
      const projectData = {
        title: safeString(data.title),
        short_description: safeString(data.short_description),
        full_description: safeString(data.full_description),
        category_id: safeNumber(data.category_id),
        location: safeString(data.location),
        latitude: Number(selectedLatitude) || 0,
        longitude: Number(selectedLongitude) || 0,
        funding_goal: safeNumber(data.funding_goal),
        expected_roi_percentage: safeNumber(data.expected_roi_percentage),
        roi_duration_months: safeNumber(data.roi_duration_months),
        deadline: data.deadline ? new Date(data.deadline).toISOString() : undefined,
        start_date: data.start_date ? new Date(data.start_date).toISOString() : undefined,
        currency_code: safeString(data.currency_code),
        rules: {
          auto_close_on_goal: safeBoolean(data.auto_close_on_goal),
          min_allowed_investment: safeNumber(data.min_allowed_investment),
          max_allowed_investment: safeNumber(data.max_allowed_investment),
        },
        status: status,
      };

      // Create a completely clean object to avoid any circular references
      const cleanProjectData = JSON.parse(JSON.stringify(projectData));



      // Debug: Log the project data being sent
      console.log('Sending project data:', cleanProjectData);

      // Check if user is authenticated
      const token = localStorage.getItem('diasporawise_admin_token');
      if (!token) {
        toast.error('You are not authenticated. Please login again.');
        return;
      }

      const result = await createProject(cleanProjectData).unwrap();

      if (result.success && result.data && result.data.project_id) {
        // If Farm Sponsorship Mode, auto-register farm project configuration
        if (projectTypeMode === 'farm') {
          try {
            const adminToken = localStorage.getItem('diasporawise_admin_token') || localStorage.getItem('token');
            await fetch(`${API_BASE_URL}/admin/farm-projects`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
              body: JSON.stringify({
                project_id: result.data.project_id,
                sponsorship_capacity: Number(farmCapacity) || 150,
                benefit_options: ['produce', 'rita_credit'],
                estimated_harvest_at: farmHarvestDate ? new Date(farmHarvestDate).toISOString() : null,
                is_sponsorship_open: true
              })
            });
            toast.success('Farm Sponsorship Specs & Agriculture Category registered automatically!');
          } catch (fErr) {
            console.error('Failed to auto-configure farm project specs:', fErr);
          }
        }

        toast.success(result?.message || 'Project created successfully! Now you can add images.');
        reset();
        refetchProjects();
        setSelectedLatitude(null);
        setSelectedLongitude(null);
        setSelectedLocation('');
        setLocationSearch('');

        // Switch to images tab
        setActiveTab('images');
      } else {
        toast.error(result?.message || 'Failed to create project');
      }
    } catch (error: any) {
      console.error('Project creation error:', error);

      // Handle specific error cases
      if (error?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        // Optionally redirect to login
        // navigate('/admin');
      } else if (error?.status === 403) {
        toast.error('You do not have permission to create projects.');
      } else if (error?.status === 422) {
        toast.error('Invalid project data. Please check all fields.');
      } else {
        toast.error(error?.data?.message || 'An error occurred while creating the project');
      }
    }
  };

  // Function to test authentication
  const testAuthentication = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/dashboard-details`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('diasporawise_admin_token')}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Auth test response status:', response.status);
      return response.status === 200;
    } catch (error) {
      console.error('Auth test failed:', error);
      return false;
    }
  };

  const onSubmitImages = async () => {
    if (!selectedProjectForImages || selectedImages.length === 0) {
      toast.error('Please select a project and upload at least one image');
      return;
    }

    // Check if user is authenticated
    const token = localStorage.getItem('diasporawise_admin_token');
    console.log('Current token:', token ? token.substring(0, 20) + '...' : 'NO TOKEN');

    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      return;
    }

    // Validate token format
    if (token.length < 10) {
      toast.error('Invalid authentication token. Please login again.');
      return;
    }

    // Check if token looks like a JWT (has dots)
    if (!token.includes('.')) {
      toast.error('Invalid token format. Please login again.');
      return;
    }

    // Test authentication before proceeding
    console.log('Testing authentication...');
    const isAuthenticated = await testAuthentication();
    if (!isAuthenticated) {
      toast.error('Authentication failed. Please login again.');
      return;
    }
    console.log('Authentication test passed!');

    try {
      console.log('Uploading images for project:', selectedProjectForImages);
      console.log('Images to upload:', selectedImages);

      // Validate images before upload
      const validImages = selectedImages.filter(image => {
        const isValidType = image.type.startsWith('image/');
        const isValidSize = image.size <= 10 * 1024 * 1024; // 10MB limit

        // Check for supported image formats
        const supportedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        const isSupportedFormat = supportedFormats.includes(image.type.toLowerCase());

        if (!isValidType) {
          console.error('Invalid image type:', image.type);
        }
        if (!isSupportedFormat) {
          console.error('Unsupported image format:', image.type, '- Please use JPG, PNG, GIF, or WebP');
        }
        if (!isValidSize) {
          console.error('Image too large:', image.size);
        }

        return isValidType && isValidSize && isSupportedFormat;
      });

      if (validImages.length === 0) {
        toast.error('No valid images found. Please ensure images are JPG, PNG, or GIF and under 10MB each.');
        return;
      }

      if (validImages.length !== selectedImages.length) {
        toast.warning(`Only ${validImages.length} of ${selectedImages.length} images are valid. Uploading valid images only.`);
      }

      const result = await addImages({
        project_id: parseInt(selectedProjectForImages),
        images: validImages
      }).unwrap();

      // With baseQuery fix, success: false responses are now treated as errors
      // But keep this check as a safety measure
      if (result && result.success === false) {
        toast.error(result.message || 'Failed to upload images');
        return;
      }

      toast.success('Images uploaded successfully! Now you can add documents.');
      setSelectedImages([]);
      setSelectedProjectForImages('');

      // Switch to documents tab
      setActiveTab('documents');
    } catch (error: any) {
      console.error('Image upload error:', error);

      // Extract error message from various possible structures
      let errorMessage = 'An error occurred while uploading images';
      
      if (error?.data) {
        // Check if error.data has message (from success: false response)
        if (error.data.message) {
          errorMessage = error.data.message;
        } else if (error.data.error) {
          errorMessage = error.data.error;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Handle specific error cases
      if (error?.status === 400 || error?.data?.status === 400) {
        toast.error(errorMessage || 'Invalid request format. Please check the image files and project ID.');
      } else if (error?.status === 401 || error?.data?.status === 401) {
        toast.error('Authentication failed. Please login again.');
      } else if (error?.status === 413 || error?.data?.status === 413) {
        toast.error('File too large. Please reduce image size.');
      } else if (error?.status === 415 || error?.data?.status === 415) {
        toast.error('Unsupported file type. Please use JPG, PNG, or GIF images.');
      } else {
        toast.error(`Upload failed: ${errorMessage}`);
      }
    }
  };

  const onSubmitDocuments = async () => {
    if (!selectedProjectForDocuments || selectedDocuments.length === 0) {
      toast.error('Please select a project and upload at least one document');
      return;
    }
    if (!documentNameInput.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    // Check authentication before proceeding
    const token = localStorage.getItem('diasporawise_admin_token');
    if (!token) {
      toast.error('You are not authenticated. Please login again.');
      return;
    }

    // Validate token format
    if (token.length < 10 || !token.includes('.')) {
      toast.error('Invalid authentication token. Please login again.');
      return;
    }

    try {
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < selectedDocuments.length; i++) {
        const doc = selectedDocuments[i];
        try {
          // Create unique document name for each file
          const uniqueDocumentName = selectedDocuments.length === 1
            ? documentNameInput
            : `${documentNameInput} - ${doc.name}`;

          console.log('Uploading document:', {
            project_id: selectedProjectForDocuments,
            document_name: uniqueDocumentName,
            document: doc.file.name,
            document_size: doc.file.size,
            document_type: doc.file.type,
            index: i + 1,
            total: selectedDocuments.length
          });

          const result = await addDocuments({
            project_id: selectedProjectForDocuments,
            document_name: uniqueDocumentName,
            document: doc.file
          }).unwrap();

          console.log('Document upload result:', result);

          if (result.success) {
            successCount++;
            toast.success(`Document "${doc.file.name}" uploaded successfully!`);
          } else {
            errorCount++;
            toast.error(`Failed to upload "${doc.file.name}": ${result.message || 'Unknown error'}`);
          }
        } catch (error: any) {
          errorCount++;
          console.error('Document upload error:', error);

          // Handle specific error cases
          if (error?.status === 401) {
            toast.error('Authentication failed. Your session may have expired. Please login again.');
            return; // Stop processing remaining documents
          } else if (error?.status === 400) {
            toast.error(`Bad Request for "${doc.file.name}": Please check the document file and try again.`);
            console.error('400 Error details:', error?.data);
          } else if (error?.status === 413) {
            toast.error(`File "${doc.file.name}" is too large. Please reduce file size.`);
          } else if (error?.status === 415) {
            toast.error(`Unsupported file type for "${doc.file.name}". Please use PDF, DOC, DOCX, TXT, or image files.`);
          } else {
            const errorMessage = error?.data?.message || error?.message || 'Unknown error';
            toast.error(`Failed to upload "${doc.file.name}": ${errorMessage}`);
          }
        }

        // Add small delay between uploads to prevent race conditions
        if (i < selectedDocuments.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      if (successCount > 0) {
        if (errorCount === 0) {
          toast.success('All documents uploaded successfully! Project creation completed.');
        } else {
          toast.warning(`${successCount} documents uploaded successfully. ${errorCount} failed.`);
        }

        // Clear form only if at least one document was successful
        setSelectedDocuments([]);
        setSelectedProjectForDocuments('');
        setDocumentNameInput('');

        setTimeout(() => {
          toast.success('Project creation process completed! You can now view your project in the projects list.', {
            duration: 5000,
          });
        }, 1000);
      } else {
        toast.error('Failed to upload any documents. Please check your authentication and try again.');
      }
    } catch (error: any) {
      console.error('General document upload error:', error);
      toast.error('An unexpected error occurred while uploading documents. Please try again.');
    }
  };

  const isProjectFormValid = () => {
    return isValid &&
      watchedValues.title &&
      watchedValues.short_description &&
      watchedValues.full_description &&
      watchedValues.category_id &&
      watchedValues.location &&
      watchedValues.funding_goal &&
      watchedValues.min_allowed_investment &&
      watchedValues.max_allowed_investment &&
      selectedLatitude !== null &&
      selectedLongitude !== null;
  };

  const TabButton: React.FC<{
    tab: 'create' | 'images' | 'documents';
    icon: React.ReactNode;
    label: string;
    isActive: boolean;
    onClick: () => void;
  }> = ({ tab, icon, label, isActive, onClick }) => (
    <button
      onClick={onClick}
      className={`flex items-center space-x-1 sm:space-x-2 px-4 py-3.5 text-sm sm:text-base font-semibold transition-all relative border-b-2 -mb-[1px] ${isActive
        ? 'text-blue-600 border-blue-600'
        : 'text-gray-500 hover:text-gray-900 border-transparent hover:border-gray-200'
        }`}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 px-4 flex gap-2 overflow-x-auto rounded-t-xl">
        <TabButton
          tab="create"
          icon={<Plus className={`w-4 h-4 ${activeTab === 'create' ? 'text-blue-600' : 'text-gray-400'}`} />}
          label="Create Project"
          isActive={activeTab === 'create'}
          onClick={() => setActiveTab('create')}
        />
        <TabButton
          tab="images"
          icon={<ImageIcon className={`w-4 h-4 ${activeTab === 'images' ? 'text-blue-600' : 'text-gray-400'}`} />}
          label="Add Images"
          isActive={activeTab === 'images'}
          onClick={() => setActiveTab('images')}
        />
        <TabButton
          tab="documents"
          icon={<FileText className={`w-4 h-4 ${activeTab === 'documents' ? 'text-blue-600' : 'text-gray-400'}`} />}
          label="Add Documents"
          isActive={activeTab === 'documents'}
          onClick={() => setActiveTab('documents')}
        />
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow-sm">
          {activeTab === 'create' && (
          <form onSubmit={handleSubmit(onSubmitProject)} className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Project Type Selector */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50/60 p-4 rounded-2xl border border-emerald-200/80 shadow-xs mb-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h4 className="text-sm font-extrabold text-gray-900">Select Project Type & Workflow</h4>
                    <p className="text-xs text-gray-500">Choosing "Farm Sponsorship Project" auto-applies Agriculture Category and simplifies non-relevant investment fields.</p>
                  </div>
                  {projectTypeMode === 'farm' && (
                    <span className="px-3 py-1 bg-emerald-600 text-white text-[10px] uppercase font-extrabold rounded-full shadow-xs">
                      Category: Agriculture (Auto-Applied)
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setProjectTypeMode('standard')}
                    className={`p-3.5 rounded-xl text-xs sm:text-sm font-extrabold flex items-center justify-center gap-2 border transition-all ${
                      projectTypeMode === 'standard'
                        ? 'bg-white border-blue-600 text-blue-700 shadow-sm ring-2 ring-blue-100'
                        : 'bg-white/70 border-gray-200 text-gray-600 hover:bg-white'
                    }`}
                  >
                    <Building2 className="w-4.5 h-4.5" /> Standard Real Estate / Bond Project
                  </button>
                  <button
                    type="button"
                    onClick={() => setProjectTypeMode('farm')}
                    className={`p-3.5 rounded-xl text-xs sm:text-sm font-extrabold flex items-center justify-center gap-2 border transition-all ${
                      projectTypeMode === 'farm'
                        ? 'bg-emerald-600 border-emerald-700 text-white shadow-md ring-2 ring-emerald-200'
                        : 'bg-white/70 border-gray-200 text-gray-600 hover:bg-white'
                    }`}
                  >
                    <Building2 className="w-4.5 h-4.5" /> Farm Sponsorship Project
                  </button>
                </div>
              </div>

              {/* Farm Sponsorship Specs Section (Visible in Farm Mode) */}
              {projectTypeMode === 'farm' && (
                <div className="bg-emerald-50/50 p-4 rounded-2xl border border-emerald-200 space-y-4">
                  <h4 className="text-sm font-extrabold text-emerald-900 flex items-center gap-2">
                    🌾 Farm Sponsorship Configuration Specs
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Sponsorship Limit (Capacity Units) *
                      </label>
                      <input
                        type="number"
                        required
                        value={farmCapacity}
                        onChange={(e) => setFarmCapacity(e.target.value)}
                        placeholder="e.g. 150 Units"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white font-semibold"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1">
                        Estimated Harvest Date
                      </label>
                      <input
                        type="date"
                        value={farmHarvestDate}
                        onChange={(e) => setFarmHarvestDate(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white font-semibold"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                {/* Basic Information */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Project Title *
                  </label>
                  <input
                    type="text"
                    {...register('title')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter project title"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

                <div className="relative">
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Location *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={locationSearch}
                      onChange={(e) => handleLocationSearch(e.target.value)}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 pl-8 sm:pl-10 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Search for location"
                    />
                    <MapPin className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                    {isSearchingLocation && (
                      <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                  {showLocationSuggestions && locationSuggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {locationSuggestions.map((location) => (
                        <button
                          key={location.place_id}
                          type="button"
                          onClick={() => selectLocation(location)}
                          className="w-full px-3 sm:px-4 py-2 text-left hover:bg-gray-100 flex items-center space-x-2"
                        >
                          <MapPin className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />
                          <div className="text-left">
                            <div className="font-medium text-sm sm:text-base">{location.structured_formatting.main_text}</div>
                            <div className="text-xs sm:text-sm text-gray-500">{location.structured_formatting.secondary_text}</div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedLatitude && selectedLongitude && (
                    <p className="text-green-600 text-xs sm:text-sm mt-1">
                      ✓ Location coordinates: {selectedLatitude.toFixed(6)}, {selectedLongitude.toFixed(6)}
                    </p>
                  )}
                  {errors.location && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.location.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Category *
                  </label>
                  <select
                    {...register('category_id')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category_id && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.category_id.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Deadline (Optional)
                  </label>
                  <input
                    type="date"
                    {...register('deadline')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Start Date (Optional)
                  </label>
                  <input
                    type="date"
                    {...register('start_date')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Funding Goal ($) *
                  </label>
                  <input
                    type="number"
                    {...register('funding_goal')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter funding goal"
                  />
                  {errors.funding_goal && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.funding_goal.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Minimum Investment ($) *
                  </label>
                  <input
                    type="number"
                    {...register('min_allowed_investment')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter minimum investment"
                  />
                  {errors.min_allowed_investment && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.min_allowed_investment.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Maximum Investment ($) *
                  </label>
                  <input
                    type="number"
                    {...register('max_allowed_investment')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter maximum investment"
                  />
                  {errors.max_allowed_investment && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.max_allowed_investment.message}</p>
                  )}
                </div>

                <div className="sm:col-span-2 lg:col-span-3">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      {...register('auto_close_on_goal')}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      Auto-close project when funding goal is reached
                    </span>
                  </label>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Project Status
                  </label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value as 'active' | 'closed' | 'draft')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="closed">Closed</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Expected ROI Percentage (%) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('expected_roi_percentage')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter expected ROI percentage"
                  />
                  {errors.expected_roi_percentage && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.expected_roi_percentage.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    ROI Duration (Months) *
                  </label>
                  <input
                    type="number"
                    {...register('roi_duration_months')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter ROI duration in months"
                  />
                  {errors.roi_duration_months && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.roi_duration_months.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                    Currency Code *
                  </label>
                  <select
                    {...register('currency_code')}
                    className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select currency</option>
                    <option value="USD">USD - US Dollar</option>
                    <option value="EUR">EUR - Euro</option>
                    <option value="GBP">GBP - British Pound</option>
                    <option value="INR">INR - Indian Rupee</option>
                    <option value="NGN">NGN - Nigerian Naira</option>
                    <option value="CAD">CAD - Canadian Dollar</option>
                    <option value="AUD">AUD - Australian Dollar</option>
                    <option value="JPY">JPY - Japanese Yen</option>
                    <option value="CNY">CNY - Chinese Yuan</option>
                    <option value="AED">AED - UAE Dirham</option>
                  </select>
                  {errors.currency_code && (
                    <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.currency_code.message}</p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Short Description *
                </label>
                <textarea
                  {...register('short_description')}
                  rows={3}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter short description"
                />
                {errors.short_description && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.short_description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Full Description *
                </label>
                <textarea
                  {...register('full_description')}
                  rows={5}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter detailed description"
                />
                {errors.full_description && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">{errors.full_description.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 sm:pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={!isProjectFormValid() || isCreatingProject}
                  className="flex items-center space-x-2 py-2 sm:py-3 px-4 sm:px-6 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreatingProject ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <span>Create Project</span>
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        )}

        {activeTab === 'images' && (
          <div className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Project Selection */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Select Project *
                </label>
                <select
                  value={selectedProjectForImages}
                  onChange={(e) => setSelectedProjectForImages(Number(e.target.value) || '')}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a project to add images</option>
                  {projects?.map((project) => (
                    <option key={project.project_id} value={project.project_id}>
                      ID: {project.project_id} - {project.title}
                    </option>
                  ))}
                </select>
                {projects.length === 0 && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">No projects available. Create a project first.</p>
                )}
              </div>

              {/* Image Upload Form */}
              {selectedProjectForImages && (
                <ImageUploadForm
                  projectId={selectedProjectForImages}
                  onSuccess={() => {
                    toast.success('Images uploaded successfully!');
                    setSelectedProjectForImages('');
                    setActiveTab('documents');
                  }}
                  onError={(error) => {
                    console.error('Image upload error:', error);
                  }}
                />
              )}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="p-4 sm:p-6">
            <div className="space-y-4 sm:space-y-6">
              {/* Project Selection */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Select Project *
                </label>
                <select
                  value={selectedProjectForDocuments}
                  onChange={(e) => setSelectedProjectForDocuments(e.target.value)}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a project to add documents</option>
                  {projects.map((project) => (
                    <option key={project.project_id} value={project.project_id}>
                      ID: {project.project_id} - {project.title}
                    </option>
                  ))}
                </select>
                {projects.length === 0 && (
                  <p className="text-xs sm:text-sm text-gray-500 mt-1">No projects available. Create a project first.</p>
                )}
              </div>

              {/* Document Name Input */}
              <div className="mb-4">
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Document Name *</label>
                <input
                  type="text"
                  className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={documentNameInput}
                  onChange={e => setDocumentNameInput(e.target.value)}
                  placeholder="Enter document name"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {selectedDocuments.length > 1
                    ? `Multiple documents will be named: "${documentNameInput} - [filename]"`
                    : `Single document will be named: "${documentNameInput}"`
                  }
                </p>
              </div>

              {/* Document Upload */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Documents (Optional) (Max 5 documents)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-6 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.zip,.rar"
                    onChange={handleDocumentUpload}
                    className="hidden"
                    id="document-upload"
                  />
                  <label htmlFor="document-upload" className="cursor-pointer">
                    <FileText className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                    <p className="mt-2 text-xs sm:text-sm text-gray-600">
                      Click to upload documents or drag and drop
                    </p>
                    <p className="text-xs text-gray-500">PDF, DOC, DOCX, XLS, XLSX, TXT, JPG, PNG, GIF, ZIP, RAR up to 10MB each</p>
                  </label>
                </div>

                {/* Selected Documents */}
                {selectedDocuments.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2">Selected Documents ({selectedDocuments.length})</h4>
                    <div className="space-y-2">
                      {selectedDocuments.map((document, index) => (
                        <div key={index} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                            <div>
                              <p className="text-xs sm:text-sm font-medium text-gray-900">{document.name}</p>
                              <p className="text-xs text-gray-500">{(document.file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-3 h-3 sm:w-4 sm:h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 sm:pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onSubmitDocuments}
                  disabled={!selectedProjectForDocuments || selectedDocuments.length === 0 || isAddingDocuments}
                  className="flex items-center space-x-2 py-2 sm:py-3 px-4 sm:px-6 bg-blue-600 text-white text-sm sm:text-base rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {isAddingDocuments ? (
                    <>
                      <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-white"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <span>Upload Documents</span>
                      <Upload className="w-3 h-3 sm:w-4 sm:h-4" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateProject; 
